import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Switch } from '@/components/ui/Switch/Switch';
import { Loader2, Users, Moon, Settings2, DollarSign, X, AlertTriangle } from 'lucide-react';
import { useNotificationStore } from '@/stores/notificationStore';
import { BudgetService, type ProposalBudgetFull, type VenueRoomBreakdown, type VenueActivitiesBreakdown, type VenueMealsBreakdown, type VenueMeetingsBreakdown, type ExchangeRates } from '@/services/BudgetService';
import { ResearchService } from '@/services/ResearchService';
import { BudgetSummary } from './BudgetSummary';
import { VenueBudgetTab } from './VenueBudgetTab';
import { SettingsPanel } from './SettingsPanel';
import type {
  BudgetManagerProps,
  BudgetTab,
  BudgetSettings,
  BudgetFormState,
  VenueFormData,
  VenueOtherCostsFormData,
  DirtyFields,
  CopyBudgetSections,
  CopyTargetVenue,
  VenueActivitiesBreakdown as VenueActivitiesBreakdownType,
  VenueMealsBreakdown as VenueMealsBreakdownType,
  VenueMeetingsBreakdown as VenueMeetingsBreakdownType,
} from './types';
import { cn } from '@/lib/utils';

const budgetService = new BudgetService();
const researchService = new ResearchService();

// Create empty other costs
const createEmptyOtherCosts = (): VenueOtherCostsFormData => ({
  flight_cost_per_attendee: null,
  ground_transport_cost: null,
  miscellaneous_costs: null,
  budget_note: null,
});

// Create empty room breakdown
const createEmptyRoomBreakdown = (nights: number): VenueRoomBreakdown => ({
  single_rooms: 0,
  single_room_rate: null,
  double_rooms: 0,
  double_room_rate: null,
  room_nights: nights,
});

const createEmptyActivitiesBreakdown = (): VenueActivitiesBreakdown => ({
  activities_number: null,
  activities_cost_per: null,
  activities_currency: 'USD',
});

const createEmptyMealsBreakdown = (): VenueMealsBreakdown => ({
  breakfast_cost_per_attendee: null,
  breakfast_count: 0,
  lunch_cost_per_attendee: null,
  lunch_count: 0,
  dinner_cost_per_attendee: null,
  dinner_count: 0,
  alcohol_cost_per_attendee: null,
  alcohol_count: 0,
  meals_currency: 'USD',
});

const createEmptyMeetingsBreakdown = (): VenueMeetingsBreakdown => ({
  meeting_room_rental: null,
  meeting_setup_fees: null,
  meeting_av_cost: null,
  number_of_meetings: 0,
  coffee_cost_per_attendee: null,
  coffee_count: 0,
  meetings_currency: 'USD',
});

// Create initial dirty fields
const createInitialDirtyFields = (): DirtyFields => ({
  settings: false,
  venues: new Set<string>(),
});

export function BudgetManager({
  isOpen,
  onClose,
  eventId,
}: BudgetManagerProps) {
  const notificationStore = useNotificationStore();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [budget, setBudget] = useState<ProposalBudgetFull | null>(null);
  const [activeTab, setActiveTab] = useState<BudgetTab>('summary');
  const [showSettings, setShowSettings] = useState(false);
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates | null>(null);
  const [missingPrerequisites, setMissingPrerequisites] = useState<string[]>([]);

  // Event currency for display (from budget.base_currency)
  const eventCurrency = useMemo(() => budget?.base_currency || 'USD', [budget?.base_currency]);

  // Central form state - ALL editable data lives here
  const [formState, setFormState] = useState<BudgetFormState>({
    settings: {
      contingency_percent: 0,
      contingency_fixed: null,
      planning_overhead_percent: 0,
      planning_overhead_fixed: null,
      profit_margin_percent: 0,
      profit_margin_fixed: null,
      budget_note: null,
      show_on_proposal: true,
    },
    venues: {},
    isDirty: false,
    dirtyFields: createInitialDirtyFields(),
  });

  // Load ALL budget data upfront when modal opens
  useEffect(() => {
    if (!isOpen || !eventId) return;

    async function loadAllData() {
      try {
        setIsLoading(true);

        // 1. Get approved venues list to filter budget data
        const approvedProposals = await researchService.getSubmittedProposals(eventId, 'approved');
        const approvedShortlistIds = new Set<string>();
        approvedProposals.grouped_by_city.forEach((cityGroup) => {
          cityGroup.destinations.forEach((dest) => {
            dest.shortlist.forEach((item) => {
              approvedShortlistIds.add(item.id);
            });
          });
        });

        // 2. Get main budget data and exchange rates in parallel
        const [budgetData, rates] = await Promise.all([
          budgetService.getFullBudget(eventId),
          budgetService.getExchangeRates(),
        ]);
        
        setExchangeRates(rates);
        
        // 3. Filter to only include approved venues
        const filteredBudgetData: ProposalBudgetFull = {
          ...budgetData,
          venues: budgetData.venues.filter((venue) => approvedShortlistIds.has(venue.shortlist_id)),
          summary: budgetData.summary.filter((s) => approvedShortlistIds.has(s.shortlist_id)),
        };
        
        setBudget(filteredBudgetData);

        // 3.5 Check prerequisites
        const missing: string[] = [];
        if (!filteredBudgetData.base_currency) {
          missing.push('currency');
        }
        if (!filteredBudgetData.attendees || filteredBudgetData.attendees <= 0) {
          missing.push('attendees');
        }
        setMissingPrerequisites(missing);

        // If prerequisites are missing, don't load the rest of the data
        if (missing.length > 0) {
          setIsLoading(false);
          return;
        }

        // 4. Load all breakdowns for all venues in parallel
        const breakdownPromises = filteredBudgetData.venues.map(async (venue) => {
          try {
            const [roomBreakdown, activitiesBreakdown, mealsBreakdown, meetingsBreakdown] = await Promise.all([
              budgetService.getVenueRoomBreakdown(venue.shortlist_id),
              budgetService.getVenueActivitiesBreakdown(venue.shortlist_id),
              budgetService.getVenueMealsBreakdown(venue.shortlist_id),
              budgetService.getVenueMeetingsBreakdown(venue.shortlist_id),
            ]);
            return {
              shortlistId: venue.shortlist_id,
              roomBreakdown: roomBreakdown ?? createEmptyRoomBreakdown(filteredBudgetData.nights),
              activitiesBreakdown: activitiesBreakdown ?? createEmptyActivitiesBreakdown(),
              mealsBreakdown: mealsBreakdown ?? createEmptyMealsBreakdown(),
              meetingsBreakdown: meetingsBreakdown ?? createEmptyMeetingsBreakdown(),
            };
          } catch (error) {
            console.error(`Failed to load breakdowns for ${venue.shortlist_id}:`, error);
            return {
              shortlistId: venue.shortlist_id,
              roomBreakdown: createEmptyRoomBreakdown(filteredBudgetData.nights),
              activitiesBreakdown: createEmptyActivitiesBreakdown(),
              mealsBreakdown: createEmptyMealsBreakdown(),
              meetingsBreakdown: createEmptyMeetingsBreakdown(),
            };
          }
        });

        const breakdowns = await Promise.all(breakdownPromises);

        // 5. Build venue form data (only for approved venues)
        const venueFormDataMap: Record<string, VenueFormData> = {};
        filteredBudgetData.venues.forEach((venue) => {
          const destinationKey = `${venue.destination_city}:${venue.destination_country}`;
          const breakdown = breakdowns.find((b) => b.shortlistId === venue.shortlist_id);
          const venueCurrency = venue.venue_currency || 'USD';

          // Ensure breakdown currency fields match venue currency (always sync on load)
          const activitiesBreakdown = breakdown?.activitiesBreakdown ?? createEmptyActivitiesBreakdown();
          const mealsBreakdown = breakdown?.mealsBreakdown ?? createEmptyMealsBreakdown();
          const meetingsBreakdown = breakdown?.meetingsBreakdown ?? createEmptyMeetingsBreakdown();

          venueFormDataMap[venue.shortlist_id] = {
            shortlistId: venue.shortlist_id,
            destinationKey,
            venueCurrency,
            roomBreakdown: breakdown?.roomBreakdown ?? createEmptyRoomBreakdown(filteredBudgetData.nights),
            activitiesBreakdown: {
              ...activitiesBreakdown,
              activities_currency: venueCurrency, // Always sync with venue currency
            },
            mealsBreakdown: {
              ...mealsBreakdown,
              meals_currency: venueCurrency, // Always sync with venue currency
            },
            meetingsBreakdown: {
              ...meetingsBreakdown,
              meetings_currency: venueCurrency, // Always sync with venue currency
            },
            // Use venue-level other costs from backend
            otherCosts: {
              flight_cost_per_attendee: venue.venue_flight_cost_per_attendee,
              ground_transport_cost: venue.venue_ground_transport_cost,
              miscellaneous_costs: venue.venue_miscellaneous_costs,
              budget_note: venue.budget_note ?? null,
            },
          };
        });

        // 6. Initialize form state with all loaded data
        setFormState({
          settings: { ...filteredBudgetData.settings },
          venues: venueFormDataMap,
          isDirty: false,
          dirtyFields: createInitialDirtyFields(),
        });

      } catch (error) {
        console.error('Failed to load budget:', error);
        notificationStore.error('Failed to load budget data', { title: 'Error' });
      } finally {
        setIsLoading(false);
      }
    }

    loadAllData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- notificationStore methods are stable; including the store object re-triggers load on every notification
  }, [isOpen, eventId]);

  // Handle settings change - update form state only
  const handleSettingsChange = useCallback((newSettings: Partial<BudgetSettings>) => {
    setFormState((prev) => ({
      ...prev,
      settings: { ...prev.settings, ...newSettings },
      isDirty: true,
      dirtyFields: {
        ...prev.dirtyFields,
        settings: true,
      },
    }));
  }, []);

  // Handle room breakdown change for a specific venue
  const handleRoomBreakdownChange = useCallback((shortlistId: string, data: Partial<VenueRoomBreakdown>) => {
    setFormState((prev) => {
      const newDirtyVenues = new Set(prev.dirtyFields.venues);
      newDirtyVenues.add(shortlistId);

      return {
        ...prev,
        venues: {
          ...prev.venues,
          [shortlistId]: {
            ...prev.venues[shortlistId],
            roomBreakdown: { ...prev.venues[shortlistId].roomBreakdown, ...data },
          },
        },
        isDirty: true,
        dirtyFields: {
          ...prev.dirtyFields,
          venues: newDirtyVenues,
        },
      };
    });
  }, []);

  // Handle activities breakdown change for a specific venue
  const handleActivitiesBreakdownChange = useCallback((shortlistId: string, data: Partial<VenueActivitiesBreakdownType>) => {
    setFormState((prev) => {
      const newDirtyVenues = new Set(prev.dirtyFields.venues);
      newDirtyVenues.add(shortlistId);

      // Ensure activities_currency matches venueCurrency
      const venueCurrency = prev.venues[shortlistId]?.venueCurrency || 'USD';
      const updatedData = {
        ...data,
        activities_currency: venueCurrency,
      };

      return {
        ...prev,
        venues: {
          ...prev.venues,
          [shortlistId]: {
            ...prev.venues[shortlistId],
            activitiesBreakdown: { ...prev.venues[shortlistId].activitiesBreakdown, ...updatedData },
          },
        },
        isDirty: true,
        dirtyFields: {
          ...prev.dirtyFields,
          venues: newDirtyVenues,
        },
      };
    });
  }, []);

  // Handle meals breakdown change for a specific venue
  const handleMealsBreakdownChange = useCallback((shortlistId: string, data: Partial<VenueMealsBreakdownType>) => {
    setFormState((prev) => {
      const newDirtyVenues = new Set(prev.dirtyFields.venues);
      newDirtyVenues.add(shortlistId);

      // Ensure meals_currency matches venueCurrency
      const venueCurrency = prev.venues[shortlistId]?.venueCurrency || 'USD';
      const updatedData = {
        ...data,
        meals_currency: venueCurrency,
      };

      return {
        ...prev,
        venues: {
          ...prev.venues,
          [shortlistId]: {
            ...prev.venues[shortlistId],
            mealsBreakdown: { ...prev.venues[shortlistId].mealsBreakdown, ...updatedData },
          },
        },
        isDirty: true,
        dirtyFields: {
          ...prev.dirtyFields,
          venues: newDirtyVenues,
        },
      };
    });
  }, []);

  // Handle meetings breakdown change for a specific venue
  const handleMeetingsBreakdownChange = useCallback((shortlistId: string, data: Partial<VenueMeetingsBreakdownType>) => {
    setFormState((prev) => {
      const newDirtyVenues = new Set(prev.dirtyFields.venues);
      newDirtyVenues.add(shortlistId);

      // Ensure meetings_currency matches venueCurrency
      const venueCurrency = prev.venues[shortlistId]?.venueCurrency || 'USD';
      const updatedData = {
        ...data,
        meetings_currency: venueCurrency,
      };

      return {
        ...prev,
        venues: {
          ...prev.venues,
          [shortlistId]: {
            ...prev.venues[shortlistId],
            meetingsBreakdown: { ...prev.venues[shortlistId].meetingsBreakdown, ...updatedData },
          },
        },
        isDirty: true,
        dirtyFields: {
          ...prev.dirtyFields,
          venues: newDirtyVenues,
        },
      };
    });
  }, []);

  // Handle venue currency change for a specific venue
  const handleVenueCurrencyChange = useCallback((shortlistId: string, currency: string) => {
    setFormState((prev) => {
      const newDirtyVenues = new Set(prev.dirtyFields.venues);
      newDirtyVenues.add(shortlistId);

      const venueData = prev.venues[shortlistId];
      if (!venueData) return prev;

      // Update currency in all breakdowns to match the new venue currency
      return {
        ...prev,
        venues: {
          ...prev.venues,
          [shortlistId]: {
            ...venueData,
            venueCurrency: currency,
            activitiesBreakdown: {
              ...venueData.activitiesBreakdown,
              activities_currency: currency,
            },
            mealsBreakdown: {
              ...venueData.mealsBreakdown,
              meals_currency: currency,
            },
            meetingsBreakdown: {
              ...venueData.meetingsBreakdown,
              meetings_currency: currency,
            },
          },
        },
        isDirty: true,
        dirtyFields: {
          ...prev.dirtyFields,
          venues: newDirtyVenues,
        },
      };
    });
  }, []);

  // Handle budget note change for a specific venue
  const handleBudgetNoteChange = useCallback(
    (shortlistId: string, note: string | null) => {
      setFormState((prev) => {
        const newDirtyVenues = new Set(prev.dirtyFields.venues);
        newDirtyVenues.add(shortlistId);

        const venueData = prev.venues[shortlistId];
        if (!venueData) return prev;

        return {
          ...prev,
          venues: {
            ...prev.venues,
            [shortlistId]: {
              ...venueData,
              otherCosts: {
                ...venueData.otherCosts,
                budget_note: note,
              },
            },
          },
          isDirty: true,
          dirtyFields: {
            ...prev.dirtyFields,
            venues: newDirtyVenues,
          },
        };
      });
    },
    []
  );

  // Handle other costs change for a specific venue (flight, ground transport, miscellaneous)
  const handleOtherCostsChange = useCallback(
    (shortlistId: string, data: Partial<VenueOtherCostsFormData>) => {
      setFormState((prev) => {
        const newDirtyVenues = new Set(prev.dirtyFields.venues);
        newDirtyVenues.add(shortlistId);

        const venueData = prev.venues[shortlistId];
        if (!venueData) return prev;

        return {
          ...prev,
          venues: {
            ...prev.venues,
            [shortlistId]: {
              ...venueData,
              otherCosts: {
                ...venueData.otherCosts,
                ...data,
              },
            },
          },
          isDirty: true,
          dirtyFields: {
            ...prev.dirtyFields,
            venues: newDirtyVenues,
          },
        };
      });
    },
    []
  );

  // Handle copying budget sections from one venue to others
  const handleCopyBudget = useCallback(
    (sourceShortlistId: string, targetShortlistIds: string[], sections: CopyBudgetSections) => {
      setFormState((prev) => {
        const source = prev.venues[sourceShortlistId];
        if (!source) return prev;

        const newVenues = { ...prev.venues };
        const newDirtyVenues = new Set(prev.dirtyFields.venues);

        for (const targetId of targetShortlistIds) {
          const target = prev.venues[targetId];
          if (!target) continue;

          const updated = { ...target };

          if (sections.meals) {
            updated.mealsBreakdown = { ...source.mealsBreakdown };
          }
          if (sections.meetings) {
            updated.meetingsBreakdown = { ...source.meetingsBreakdown };
          }
          if (sections.activities) {
            updated.activitiesBreakdown = { ...source.activitiesBreakdown };
          }
          if (sections.otherCosts) {
            updated.otherCosts = {
              ...updated.otherCosts,
              flight_cost_per_attendee: source.otherCosts.flight_cost_per_attendee,
              ground_transport_cost: source.otherCosts.ground_transport_cost,
              miscellaneous_costs: source.otherCosts.miscellaneous_costs
                ? source.otherCosts.miscellaneous_costs.map((item) => ({
                    ...item,
                    id: crypto.randomUUID(),
                  }))
                : null,
            };
          }
          if (sections.budgetNote) {
            updated.otherCosts = {
              ...updated.otherCosts,
              budget_note: source.otherCosts.budget_note,
            };
          }
          if (sections.roomBreakdown) {
            updated.roomBreakdown = { ...source.roomBreakdown };
          }

          newVenues[targetId] = updated;
          newDirtyVenues.add(targetId);
        }

        return {
          ...prev,
          venues: newVenues,
          isDirty: true,
          dirtyFields: {
            ...prev.dirtyFields,
            venues: newDirtyVenues,
          },
        };
      });

      notificationStore.success(`Budget copied to ${targetShortlistIds.length} venue${targetShortlistIds.length !== 1 ? 's' : ''}`, { title: 'Copied' });
    },
    [notificationStore]
  );

  // Save only changed data
  const handleSave = async () => {
    if (!budget) return;

    try {
      setIsSaving(true);

      // 1. Save settings only if changed
      if (formState.dirtyFields.settings) {
        await budgetService.updateBudgetSettings(eventId, formState.settings);
      }

      // 2. Save only changed venues
      for (const shortlistId of formState.dirtyFields.venues) {
        const venueData = formState.venues[shortlistId];
        if (!venueData) continue;

        // Save venue currency
        await budgetService.updateVenueCurrency(shortlistId, venueData.venueCurrency);

        // Save room breakdown
        await budgetService.updateVenueRoomBreakdown(shortlistId, venueData.roomBreakdown);

        // Save activities breakdown
        await budgetService.updateVenueActivitiesBreakdown(shortlistId, venueData.activitiesBreakdown);

        // Save meals breakdown
        await budgetService.updateVenueMealsBreakdown(shortlistId, venueData.mealsBreakdown);

        // Save meetings breakdown
        await budgetService.updateVenueMeetingsBreakdown(shortlistId, venueData.meetingsBreakdown);

        // Save venue-level other costs (flight, ground transport, miscellaneous, budget note)
        await budgetService.updateVenueOtherCosts(shortlistId, {
          flight_cost_per_attendee: venueData.otherCosts.flight_cost_per_attendee,
          ground_transport_cost: venueData.otherCosts.ground_transport_cost,
          miscellaneous_costs: venueData.otherCosts.miscellaneous_costs,
          budget_note: venueData.otherCosts.budget_note,
        });
      }

      // Mark form as clean - NO RELOAD
      setFormState((prev) => ({
        ...prev,
        isDirty: false,
        dirtyFields: createInitialDirtyFields(),
      }));

      notificationStore.success('Budget saved successfully', { title: 'Saved' });
    } catch (error) {
      console.error('Failed to save budget:', error);
      notificationStore.error('Failed to save budget', { title: 'Error' });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel - discard changes and close
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle venue selection from summary
  const handleSelectVenue = useCallback((venueId: string) => {
    setActiveTab(venueId);
  }, []);

  // Get active venue budget (memoized to prevent re-renders)
  const activeVenueBudget = useMemo(
    () => budget?.venues.find((v) => v.shortlist_id === activeTab),
    [budget, activeTab]
  );

  // Get active venue form data
  const activeVenueFormData = useMemo(
    () => (activeTab !== 'summary' ? formState.venues[activeTab] : null),
    [activeTab, formState.venues]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      size="fullScreen"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          <span className="ml-3 text-gray-600">Loading budget data...</span>
        </div>
      ) : missingPrerequisites.length > 0 ? (
        <div className="flex flex-col h-full">
          {/* Header with close button */}
          <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Budget Manager</h2>
              <button
                onClick={handleCancel}
                className="rounded-md p-1.5 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                aria-label="Close modal"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Prerequisites not met content */}
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="max-w-md w-full bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center">
              <div className="mx-auto w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Budget Manager Unavailable
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Some event settings must be configured before you can manage the budget.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm font-medium text-gray-700 mb-2">Missing requirements:</p>
                <ul className="space-y-2 text-sm text-gray-600">
                  {missingPrerequisites.includes('currency') && (
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">•</span>
                      <span><strong>Event currency</strong> – needed for accurate cost display and conversions</span>
                    </li>
                  )}
                  {missingPrerequisites.includes('attendees') && (
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">•</span>
                      <span><strong>Expected attendees</strong> – required for per-person cost calculations</span>
                    </li>
                  )}
                </ul>
              </div>
              <Link
                to={`/organization/events/${eventId}`}
                onClick={handleCancel}
              >
                <Button variant="primary" className="w-full">
                  Configure Event Settings
                </Button>
              </Link>
            </div>
          </div>
        </div>
      ) : !budget ? (
        <div className="text-center py-20 text-gray-500">
          No budget data available. Ensure you have approved venues.
        </div>
      ) : (
        <div className="flex flex-col h-screen">
          {/* Header Bar */}
          <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Title and Event Info */}
              <div className="flex items-center gap-6">
                <h2 className="text-lg font-semibold text-gray-900">Budget Manager</h2>
                <div className="flex items-center gap-4 text-sm">
                  <span className="font-medium text-gray-700">{budget.event_name}</span>
                  <div className="flex items-center gap-1 text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>{budget.attendees} attendees</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-600">
                    <Moon className="w-4 h-4" />
                    <span>{budget.nights} nights</span>
                  </div>
                  {budget.base_currency && (
                    <div className="flex items-center gap-1 text-gray-600">
                      <DollarSign className="w-4 h-4" />
                      <span>Currency: {budget.base_currency}</span>
                    </div>
                  )}
                  {formState.isDirty && (
                    <span className="text-amber-600 font-medium">• Unsaved changes</span>
                  )}
                </div>
              </div>

              {/* Proposal visibility, Settings and Close */}
              <div className="flex items-center gap-3">
                {/* Same pattern as the Agenda builder's "Show on proposal"; applied on Save Changes. */}
                <Switch
                  label="Show budget on proposal"
                  checked={formState.settings.show_on_proposal !== false}
                  onChange={(e) => handleSettingsChange({ show_on_proposal: e.target.checked })}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSettings(!showSettings)}
                  className={cn(showSettings && 'bg-gray-200')}
                >
                  <Settings2 className="w-4 h-4 mr-1" />
                  Settings
                </Button>
                <button
                  onClick={handleCancel}
                  className="rounded-md p-1.5 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Settings Panel (collapsible) */}
            {showSettings && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <SettingsPanel
                  settings={formState.settings}
                  eventCurrency={eventCurrency}
                  onChange={handleSettingsChange}
                />
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6">
            <div className="flex gap-1 overflow-x-auto">
              <button
                onClick={() => setActiveTab('summary')}
                className={cn(
                  'px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
                  activeTab === 'summary'
                    ? 'border-teal-600 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                Summary
              </button>
              {budget.venues.map((venue) => (
                <button
                  key={venue.shortlist_id}
                  onClick={() => setActiveTab(venue.shortlist_id)}
                  className={cn(
                    'px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
                    activeTab === venue.shortlist_id
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  )}
                >
                  {venue.venue_name}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {activeTab === 'summary' ? (
              <div className="flex-1 overflow-y-auto p-6">
                <BudgetSummary
                  budget={budget}
                  currentSettings={formState.settings}
                  venueFormData={formState.venues}
                  eventCurrency={eventCurrency}
                  exchangeRates={exchangeRates}
                  onSelectVenue={handleSelectVenue}
                />
              </div>
            ) : activeVenueBudget && activeVenueFormData ? (
              <VenueBudgetTab
                venueBudget={activeVenueBudget}
                venueFormData={activeVenueFormData}
                attendees={budget.attendees}
                nights={budget.nights}
                settings={formState.settings}
                eventCurrency={eventCurrency}
                exchangeRates={exchangeRates}
                onRoomBreakdownChange={(data) => handleRoomBreakdownChange(activeVenueBudget.shortlist_id, data)}
                onActivitiesBreakdownChange={(data) => handleActivitiesBreakdownChange(activeVenueBudget.shortlist_id, data)}
                onMealsBreakdownChange={(data) => handleMealsBreakdownChange(activeVenueBudget.shortlist_id, data)}
                onMeetingsBreakdownChange={(data) => handleMeetingsBreakdownChange(activeVenueBudget.shortlist_id, data)}
                onVenueCurrencyChange={(currency) => handleVenueCurrencyChange(activeVenueBudget.shortlist_id, currency)}
                onOtherCostsChange={(data) => handleOtherCostsChange(activeVenueBudget.shortlist_id, data)}
                onBudgetNoteChange={(note) => handleBudgetNoteChange(activeVenueBudget.shortlist_id, note)}
                otherVenues={budget.venues
                  .filter((v) => v.shortlist_id !== activeVenueBudget.shortlist_id)
                  .map((v) => ({
                    shortlistId: v.shortlist_id,
                    venueName: v.venue_name,
                    venueCurrency: formState.venues[v.shortlist_id]?.venueCurrency || eventCurrency,
                  }))}
                onCopyBudget={(targetIds, sections) =>
                  handleCopyBudget(activeVenueBudget.shortlist_id, targetIds, sections)
                }
              />
            ) : (
              <div className="text-center text-gray-500 py-10">Venue not found</div>
            )}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                isLoading={isSaving}
                disabled={!formState.isDirty}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default BudgetManager;
